import apiClient from "./apiClient"
import type {
  SoftoneApartment,
  SoftoneBuilding,
  SoftoneBuildingLinkItem,
  SoftoneLink,
  SoftoneRequestRecord,
  SoftoneUserLinkItem,
} from "../data/softone"

// "pending" removes the link (back to showing the suggestion)
export type SoftoneLinkChoice = { status: "linked"; softonePrjc: string } | { status: "none" } | { status: "pending" }

// SoftOne is slow on a cold cache (login + 2 calls), so allow more than the default 10s
const SOFTONE_TIMEOUT = 30000

export const fetchSoftoneBuildingLinks = async (refresh = false) => {
  const res = await apiClient.get(`/softone/buildings${refresh ? "?refresh=true" : ""}`, { timeout: SOFTONE_TIMEOUT })
  return res.data as { fetchedAt: string; softoneBuildings: SoftoneBuilding[]; items: SoftoneBuildingLinkItem[] }
}

export const saveSoftoneBuildingLink = async (buildingId: string, choice: SoftoneLinkChoice) => {
  const res = await apiClient.put(`/softone/buildings/${buildingId}`, choice, { timeout: SOFTONE_TIMEOUT })
  return res.data.link as SoftoneLink | null
}

export const fetchSoftoneUserLinks = async (refresh = false) => {
  const res = await apiClient.get(`/softone/users${refresh ? "?refresh=true" : ""}`, { timeout: SOFTONE_TIMEOUT })
  return res.data as {
    fetchedAt: string
    softoneBuildings: SoftoneBuilding[]
    apartments: SoftoneApartment[]
    buildingLinks: Record<string, string | null>
    items: SoftoneUserLinkItem[]
  }
}

export const saveSoftoneUserLink = async (userId: string, choice: SoftoneLinkChoice) => {
  const res = await apiClient.put(`/softone/users/${userId}`, choice, { timeout: SOFTONE_TIMEOUT })
  return res.data.link as SoftoneLink | null
}

/** Value of a link <select>: "" = pending, "none" = no link, otherwise the SoftOne prjc. */
export const linkToSelectValue = (link: SoftoneLink | null | undefined) =>
  !link ? "" : link.status === "none" ? "none" : link.softonePrjc || ""

export const selectValueToChoice = (value: string): SoftoneLinkChoice =>
  value === "" ? { status: "pending" } : value === "none" ? { status: "none" } : { status: "linked", softonePrjc: value }

/** SoftOne send status of these tickets (missing = ticket created before sending to SoftOne existed). */
export const fetchSoftoneRequests = async (ticketIds: string[]) => {
  const requests: Record<string, SoftoneRequestRecord> = {}
  let enabled = false
  for (let i = 0; i < ticketIds.length; i += 100) {
    const res = await apiClient.get("/softone/requests", { params: { ticketIds: ticketIds.slice(i, i + 100).join(",") } })
    Object.assign(requests, res.data.requests)
    enabled = res.data.enabled
  }
  return { enabled, requests }
}

/** Admin (re)sends a failed / waiting ticket to SoftOne. */
export const sendSoftoneRequest = async (ticketId: string) => {
  const res = await apiClient.post(`/softone/requests/${ticketId}/send`, {}, { timeout: SOFTONE_TIMEOUT })
  return res.data.request as SoftoneRequestRecord | null
}
